import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"; // your Express backend

// Fetch cached exchange rates (INR base). Browser never hits Frankfurter.
// Failures return null quietly — CurrencyProvider already has FALLBACK_RATES_FROM_INR.
export const fetchExchangeRates = async () => {
  try {
    const response = await axios.get(`${API_URL}/exchange-rates`);
    const data = response.data || {};
    // Normalize: support both new shape { rates: { USD: … } } and nested legacy shapes
    const rates = data.rates && typeof data.rates === "object" ? data.rates : data;
    return {
      base: data.base || "INR",
      rates: { INR: 1, ...rates },
      lastUpdated: data.lastUpdated || data.lastFetch || null,
      isFallback: Boolean(data.isFallback || rates.isFallback),
    };
  } catch {
    return null;
  }
};

export const fetchFilterOptions = async (type = "") => {
  try {
    const response = await axios.get(`${API_URL}/filters`, {
      params: type ? { type } : undefined,
    });
    return {
      types: response.data?.types || [],
      styles: response.data?.styles || [],
      meals: response.data?.meals || [],
      sources: response.data?.sources || [],
      durations: response.data?.durations || [],
      locations: response.data?.locations || [],
    };
  } catch (error) {
    console.error("Error fetching filter options:", error);
    return { types: [], styles: [], meals: [], sources: [], durations: [], locations: [] };
  }
};

const normalizeRetreat = (item, index) => {
  const fallbackId = item.source_url || `${item.source || "unknown"}-${index}`;

  const normalizedPriceClean = (() => {
    if (typeof item.price_clean === "number") {
      return Number.isFinite(item.price_clean) ? item.price_clean : null;
    }

    if (item.price_clean == null || item.price_clean === "") return null;

    const parsed = Number(item.price_clean);
    return Number.isFinite(parsed) ? parsed : null;
  })();

  return {
    id: item.id ?? fallbackId,
    retreat_key: item.retreat_key || null,
    slug: item.slug || null,
    retreat_name: item.retreat_name || "Unknown Retreat",
    location: item.location || "Unknown Location",
    location_tags: item.location_tags || "",
    type: item.type || "Unknown",
    type_tags: item.type_tags || "",
    style: item.style || "",
    style_tags: item.style_tags || "",
    styles: item.style_tags
      ? item.style_tags.split(",").map(s => s.trim()).filter(Boolean)
      : item.style ? item.style.split(",").map(s => s.trim()).filter(Boolean) : [],
    duration: item.duration || "",
    meals: item.meals || "",
    price: item.price || "",
    price_clean: normalizedPriceClean,
    price_source_currency: item.price_source_currency,
    price_source_amount: item.price_source_amount,
    price_usd: item.price_usd,
    price_gbp: item.price_gbp,
    price_eur: item.price_eur,
    price_inr: item.price_inr,
    thumbnail_url: item.thumbnail_url,
    google_rating: item.google_rating,
    google_review_count: item.google_review_count,
    google_rating_url: item.google_rating_url,
    description: item.description || "",
    source_url: item.source_url || "",
    ingestion_date: item.ingestion_date || "",
    source: item.source || "",
    batches: Array.isArray(item.batches) ? item.batches : (typeof item.batches === 'string' ? JSON.parse(item.batches) : []),
    item_source: item.item_source || "retreat",
    instructor_name: item.instructor_name || "",
    instructor_slug: item.instructor_slug || "",
    instructor_id: item.instructor_id || "",
    schedule_summary: item.schedule_summary || "",
    schedule_json: item.schedule_json || [],
    level: item.level || "",
    format: item.format || "",
  };
};

export const searchRetreats = async ({
  nameQuery,
  locationQuery,
  type,
  excludeTypes,
  style,
  meals,
  source,
  duration,
  rating,
  maxPrice,
  sort,
  startDate,
  endDate,
  limit = 50,
  offset = 0,
} = {}) => {
  try {
    const params = {
      name: nameQuery || undefined,
      location: locationQuery || undefined,
      type: type || undefined,
      exclude_types:
        Array.isArray(excludeTypes) && excludeTypes.length > 0
          ? excludeTypes.join(",")
          : undefined,
      meals: meals || undefined,
      style: style || undefined,
      source: source || undefined,
      duration: duration || undefined,
      rating: rating || undefined,
      max_price: maxPrice || undefined,
      sort: sort || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      limit,
      offset,
    };

    const response = await axios.get(`${API_URL}/search`, { params });
    const items = (response.data?.items || []).map((item, index) => normalizeRetreat(item, index));
    const total = response.data?.total ?? items.length;
    return { total, items };
  } catch (error) {
    console.error("Error searching retreats:", error);
    return { total: 0, items: [] };
  }
};

export const fetchYogaCenters = async () => {
  try {
    const response = await axios.get(`${API_URL}/retreats`);
    return response.data.map((item, index) => normalizeRetreat(item, index));
  } catch (error) {
    console.error("Error fetching yoga centers:", error);
    return [];
  }
};

export const submitContactForm = async (payload) => {
  const response = await axios.post(`${API_URL}/contact`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const createPaymentOrder = async (payload) => {
  const response = await axios.post(`${API_URL}/api/v1/payments/create-order`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const verifyPayment = async (payload) => {
  const response = await axios.post(`${API_URL}/api/v1/payments/verify`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const fetchOfferingBySlug = async (slug) => {
  const response = await axios.get(`${API_URL}/offerings/slug/${encodeURIComponent(slug)}`);
  return response.data;
};

export const fetchRetreatBySlug = async (slug) => {
  const response = await axios.get(`${API_URL}/retreats/slug/${encodeURIComponent(slug)}`);
  return response.data;
};

export const fetchTeacherBySlug = async (slug) => {
  const response = await axios.get(`${API_URL}/teachers/slug/${encodeURIComponent(slug)}`);
  return response.data;
};
